<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0" v-if="showPartner()">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Employee ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.student_status"
          :items="statuses"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Status"
          item-text="name"
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_completed"
          :items="program_completed_statuses"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Program Completed"
          item-text="name"
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!students.length"
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="students"
            fixed-header
            item-key="student_detail_id"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                :disabled="
                  item.current_status == 'Discontinued' ||
                  item.current_status == 'Graduated' ||
                  item.ser_id
                "
                @click="editDialog(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template v-slot:[`item.remarks`]="{ item }">
              <v-icon
                v-if="item.remarks"
                small
                class="mr-2"
                color="accent"
                @click="viewRemarks(item.remarks)"
              >
                mdi-eye
              </v-icon>
              <div v-else>-</div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-if="dialog" v-model="dialog" width="800" persistent>
      <v-card class="secondary" height="100%">
        <v-form ref="form">
          <v-card-title class="font-weight-medium secondary--text primary">
            Edit Status
            <v-spacer></v-spacer>
            <v-btn small icon @click="closeDialog()">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="4"
                ><v-text-field
                  v-model="selectedStudent.usn_id"
                  label="USN"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
              /></v-col>
              <v-col cols="4"
                ><v-autocomplete
                  v-model="editStatus.changedStatus"
                  label="Status*"
                  outlined
                  dense
                  :items="formattedStatus"
                  :rules="[rules.required]"
                  hide-details="auto"
              /></v-col>
              <v-col cols="4" v-if="editStatus.changedStatus == 'Re-Admission'"
                ><v-text-field
                  v-model="editStatus.termNo"
                  label="Re-Admission Term No*"
                  outlined
                  dense
                  :rules="[
                    rules.required,
                    rules.number,
                    rules.lt(editStatus.termNo, this.program.duration),
                  ]"
                  hide-details="auto"
              /></v-col>
              <v-col cols="4" v-if="this.currentUser.partner_id != 1">
                <v-menu
                  v-model="showDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedFromDate"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      label="Date of Exit*"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="editStatus.date_of_exit"
                    :max="maxExitDate"
                    @input="showDatePicker = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editStatus.remarks"
                  class="mt-2"
                  rows="2"
                  dense
                  label="Remarks"
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pt-0 mx-2">
            <v-spacer></v-spacer>
            <v-btn class="primary" @click="checkReadmitTerminate">Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <ConfirmationDialog
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="confirmationDialog = false"
        @saveDialog="updateStudent()"
      />
    </v-dialog>
    <v-dialog v-model="showRemarksDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          Remarks
          <v-spacer></v-spacer>
          <v-btn small icon @click="showRemarksDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mt-3 reset-card-text">
          {{ remarks }}
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { studentActiveStatusHeaders } from "@/constants/headers";
import ConfirmationDialog from "@/components/confirmationDialog.vue";

export default {
  components: { ConfirmationDialog },
  data() {
    return {
      form: {
        student_status: "A",
      },
      programs: [],
      partners: [],
      statuses: [
        { name: "Active", id: "A" },
        { name: "Inactive", id: "I" },
        { name: "Discontinued", id: "T" },
        { name: "Graduated", id: "X" },
      ],
      program_completed_statuses: [
        { name: "Yes", id: "Y" },
        { name: "No", id: "N" },
      ],
      rules,
      loading: false,
      batches: [],
      headers: [],
      studentActiveStatusHeaders,
      students: [],
      selectedStudent: {},
      dialog: false,
      editStatus: {
        remarks: null,
        termNo: null,
        changedStatus: null,
      },
      program: {},
      confirmationDialog: false,
      readmitStatus: {},
      showDatePicker: false,
      showRemarksDialog: false,
      remarks: "",
    };
  },
  watch: {},
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    formattedStatus() {
      if (this.currentUser.partner_id != 1) {
        return ["Exit/Termination"];
      } else if (this.selectedStudent.current_status == "Active") {
        return ["Inactive", "Discontinued"];
      } else {
        return ["Re-Admission", "Discontinued"];
      }
    },
    confirmationText() {
      const status = this.editStatus.changedStatus;

      const messages = {
        "Exit/Termination":
          "Are you sure you want to terminate the selected student?",
        Discontinued: "Are you sure you want to discontinue the selected student?",
        "Re-Admission":
          "Are you sure you want to re-admit the selected student?",
        Inactive:
          "Are you sure you want to mark the selected student as inactive?",
      };

      return messages[status] || "";
    },
    maxExitDate() {
      return new Date().toISOString().slice(0, 10);
    },
    formattedFromDate() {
      if (!this.editStatus.date_of_exit) return "";
      const [year, month, day] = this.editStatus.date_of_exit.split("-");
      return `${day}-${month}-${year}`;
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Lifecycle" }]);

    this.headers = [...this.studentActiveStatusHeaders];

    if (this.currentUser.partner_id != 1) {
      this.headers.splice(
        this.headers.length - 1,
        0,
        {
          text: "Date Of Exit",
          value: "date_of_exit",
          class: "black--text font-weight-bold secondary",
        },
        {
          text: "Date Of Exit Raised",
          value: "exit_raised",
          class: "black--text font-weight-bold secondary",
        },
        {
          text: "Remarks",
          value: "remarks",
          class: "black--text font-weight-bold secondary",
          sortable: false,
        },
      );
    }

    await this.generateBatchList();
    this.getPrograms();
    this.showPartnerFilter();
    this.init();
  },
  methods: {
    async getStudents() {
      this.loading = true;
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      try {
        const res = await this.$axios.get(
          `/list_status_students?${queryString}`,
        );
        this.students = res.data.data;

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }

      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        ) // Remove undefined/null values
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },
    async init() {
      this.getStudents();
    },
    async getPrograms() {
      let endpoint = "/programs";
      if (this.currentUser.partner_id != 1)
        endpoint = `/get_partner_programs/${this.currentUser.partner_id}`;
      try {
        const response = await this.$axios.get(endpoint);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    showPartner() {
      return (
        this.currentUser.partner_id == 1 ||
        (this.currentUser.role === "Partner" &&
          this.partners.length > 1 &&
          this.currentUser.partner_level != "S")
      );
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async showPartnerFilter() {
      if (this.currentUser.partner_id != 1) {
        this.form.partner_id = this.currentUser.partner_id;
      }
      await this.getPartners();
      if (
        this.currentUser.role === "Partner" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id,
        );
      }
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async editDialog(item) {
      this.selectedStudent = { ...item };
      if (this.selectedStudent.current_status == "Inactive") {
        const response = await this.$axios.get(
          `/programs/${this.selectedStudent.program_id}`,
        );
        this.program = response.data.data;
      }
      if (this.currentUser.partner_id != 1) {
        this.editStatus.changedStatus = "Exit/Termination";
      }
      this.dialog = true;
    },
    closeDialog() {
      this.editStatus = {
        remarks: null,
        termNo: null,
        changedStatus: null,
      };
      this.dialog = false;
      this.confirmationDialog = false;
      this.selectedStudent = {};
      this.readmitStatus = {};
      this.program = {};
    },
    async checkReadmitTerminate() {
      if (this.editStatus.changedStatus != "Re-Admission") {
        this.confirmationDialog = true;
        return;
      }

      this.loading = true;
      try {
        const response = await this.$axios.post(
          `/term_sessions/check_readmit`,
          {
            term_type:
              this.program.duration_type == "S" ? "Semester" : "Annual",
            current_term: this.editStatus.termNo,
            program_duration: this.program.duration,
            admission_batch_code: `${this.selectedStudent.year}-${this.selectedStudent.batch}`,
            student_detail_id: this.selectedStudent.student_detail_id,
          },
        );
        this.readmitStatus = response.data.data;
        if (!this.readmitStatus.can_readmit) {
          this.$store.commit("showSnackbar", {
            message: "Student cannot be re-admitted",
            color: "error",
          });
          return;
        } else {
          this.confirmationDialog = true;
        }
      } catch (error) {
        this.loading = false;
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async updateStudent() {
      try {
        this.loading = true;

        if (this.editStatus.changedStatus == "Exit/Termination") {
          this.updateStudentStatusRequest();
          return;
        }
        let yearBatch = {};
        if (this.editStatus.changedStatus == "Re-Admission") {
          const [year, batch] =
            this.readmitStatus.readmit_batch_code.split("-");
          yearBatch = {
            year,
            batch,
          };
        }
        await this.$axios.post("/record_student_change", {
          student_detail_id: this.selectedStudent.student_detail_id,
          student_status_id: this.selectedStudent.student_status_id,
          usn_id: this.selectedStudent.usn_id,
          status: this.editStatus.changedStatus,
          remarks: this.editStatus.remarks,
          current_term_no: this.editStatus.termNo,
          ...yearBatch,
        });
        this.closeDialog();
        this.init();
        this.$store.commit("showSnackbar", {
          message: "Status Changed Successfully",
          color: "success",
        });
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async updateStudentStatusRequest() {
      try {
        await this.$axios.post("/student_exit_requests", {
          student_exit_request: {
            student_detail_id: this.selectedStudent.student_detail_id,
            usn_id: this.selectedStudent.usn_id,
            requested_user_id: this.currentUser.id,
            remarks: this.editStatus.remarks,
            date_of_exit: this.editStatus.date_of_exit,
            status: "Pending",
          },
        });
        this.closeDialog();
        this.init();
        this.$store.commit("showSnackbar", {
          message: "Exit request submitted successfully",
          color: "success",
        });
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async download() {
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      this.$axios
        .get(`/download_student_lifecycle?${queryString}`, {
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Student Lifecycle.csv");
          document.body.appendChild(link);
          link.click();
        });
    },
    viewRemarks(remarks) {
      this.remarks = remarks;
      this.showRemarksDialog = true;
    },
  },
};
</script>

<style scoped></style>
